import { useRef, useState } from "react";
import { LayoutGrid, FileText, Paperclip, Image as ImageIcon, Download, Play, Pause } from "lucide-react";
import { assetUrl } from "../api";
import { money, fmtSize } from "../quoteUtils";

// Shared by "Quote Details" (/quotes/:id) and the customer "Comments" page,
// so the Overview / Description / Attachments tabs look the same everywhere.

export const QUOTE_TABS = [
  { key: "overview", label: "Overview", Icon: LayoutGrid },
  { key: "description", label: "Description", Icon: FileText },
  { key: "attachments", label: "Attachments", Icon: Paperclip },
];

export function TabBar({ tab, onChange, compact = false }) {
  return (
    <div role="tablist" className="grid grid-cols-3 rounded-[12px] bg-[#eff0f4]">
      {QUOTE_TABS.map(({ key, label, Icon }) => (
        <button key={key} role="tab" type="button" aria-selected={tab === key} onClick={() => onChange(key)}
          className={`flex items-center justify-center gap-2 rounded-[12px] font-medium transition ${compact ? "h-[44px] text-[13px]" : "h-[60px] text-[15px]"} ${tab === key ? "bg-[#0e315c] text-white shadow-md" : "text-[#666c78] hover:text-[#0e315c]"}`}>
          <Icon size={compact ? 15 : 17} /> {label}
        </button>
      ))}
    </div>
  );
}

// rows: [label, value, alwaysShow?]; rows with no value are skipped unless alwaysShow
export const buildRows = (rows) =>
  rows.filter(([, v, always]) => always || (v !== undefined && v !== null && String(v).trim() !== ""));

export function TabPanel({ tab, rows = [], pricing, description, attachments = [], compact = false }) {
  const pad = compact ? "p-6" : "p-9";
  const h = compact ? "text-[18px]" : "text-[22px]";

  if (tab === "overview") {
    return (
      <div className="rounded-[14px] bg-[#0e2a52] px-[22px] py-5 text-white">
        <h3 className={compact ? "text-[18px] font-bold" : "text-[15px] font-bold"}>Item Details</h3>
        <div className="mt-2">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-6 border-b border-white/10 py-[11px] text-[15px] last:border-0">
              <span>{k}</span>
              <span className="text-right font-medium">{v || "—"}</span>
            </div>
          ))}
        </div>
        {pricing && (
          <div className="mt-4 space-y-1.5 rounded-xl bg-white/5 p-4 text-sm">
            <div className="flex justify-between text-white/70"><span>Parts</span><span>{money(pricing.subtotal)}</span></div>
            <div className="flex justify-between text-white/70"><span>Freight / Delivery</span><span>{money(pricing.freightTotal)}</span></div>
            <div className="flex justify-between text-white/70"><span>GST ({pricing.taxRate ?? 10}%)</span><span>{money(pricing.tax)}</span></div>
            {Number(pricing.coreChargeTotal) > 0 && (
              <div className="flex justify-between text-white/70"><span>Core charge (refundable)</span><span>{money(pricing.coreChargeTotal)}</span></div>
            )}
            <div className="flex justify-between border-t border-white/15 pt-2 font-bold"><span>Total (AUD)</span><span>{money(pricing.total)}</span></div>
          </div>
        )}
      </div>
    );
  }

  if (tab === "description") {
    return (
      <div className={`relative overflow-hidden rounded-[14px] bg-white ${pad}`}>
        <h3 className={`${h} font-bold text-[#0e1a30]`}>Quote Description</h3>
        <p className="mt-5 max-w-[420px] whitespace-pre-line text-[14px] leading-6 text-[#1b2437]">{description}</p>
        <Blueprint />
      </div>
    );
  }

  return (
    <div className={`rounded-[14px] bg-white ${pad}`}>
      <h3 className={`${h} font-bold text-[#0e1a30]`}>Attachments ({attachments.length})</h3>
      <div className="mt-5 max-w-[360px] space-y-3">
        {attachments.map((f, i) =>
          f.type === "audio" ? (
            <AudioRow key={i} src={assetUrl(f.url)} name={f.name} />
          ) : (
            <a key={i} href={assetUrl(f.url)} download target="_blank" rel="noreferrer"
              className="flex items-center gap-3 rounded-[8px] border border-[#e8eaf0] px-3 py-2.5 hover:bg-slate-50">
              <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-md ${f.type === "pdf" ? "bg-red-50 text-red-500" : "bg-blue-50 text-blue-500"}`}>
                {f.type === "image" ? <ImageIcon size={16} /> : <FileText size={16} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-[#172238]">{f.name}</span>
                <span className="block text-[10px] text-[#9aa0aa]">{fmtSize(f.size)}</span>
              </span>
              <Download size={15} className="shrink-0 text-[#172238]" />
            </a>
          )
        )}
        {attachments.length === 0 && <p className="text-sm text-slate-400">No attachments.</p>}
      </div>
    </div>
  );
}

// Audio attachment: play button + waveform + download (as in the design)
const WAVE = [6, 14, 9, 18, 8, 16, 11, 20, 7, 15, 10, 18, 6, 13, 9, 17, 8, 14, 11, 19, 7, 15, 9, 12];
export function AudioRow({ src, name }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {}); else a.pause();
  };
  return (
    <div className="flex items-center gap-3 rounded-[8px] border border-[#e8eaf0] px-3 py-2.5">
      <audio ref={ref} src={src} preload="none" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
      <button type="button" onClick={toggle} aria-label={playing ? "Pause" : "Play"} className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#2f6df6] text-white">
        {playing ? <Pause size={13} /> : <Play size={13} className="ml-0.5" />}
      </button>
      <div className="flex h-6 flex-1 items-center gap-[3px]" title={name}>
        {WAVE.map((h, i) => <span key={i} className="w-[2px] rounded-full bg-[#2f6df6]" style={{ height: h }} />)}
      </div>
      <a href={src} download target="_blank" rel="noreferrer" aria-label="Download audio"><Download size={15} className="text-[#172238]" /></a>
    </div>
  );
}

// faint line-art drawing in the corner of the Description card
export function Blueprint() {
  return (
    <svg aria-hidden="true" viewBox="0 0 240 150" className="pointer-events-none absolute bottom-0 right-0 h-[150px] w-[240px] text-[#8b8de8] opacity-40" fill="none" stroke="currentColor" strokeWidth="1">
      <circle cx="150" cy="95" r="52" /><circle cx="150" cy="95" r="38" /><circle cx="150" cy="95" r="24" /><circle cx="150" cy="95" r="10" />
      <rect x="40" y="52" width="70" height="86" rx="10" /><path d="M40 78h70M40 104h70M75 52v86" />
      <path d="M110 70l30-16M110 120l30 18M202 70l22-14M202 122l22 14" />
    </svg>
  );
}
